import * as B from '@blueprintjs/core';
import {ErrorBoundary} from '@sentry/react';
import classNames from 'classnames';
import React from 'react';

import {
  ApiBulkOrderVerification,
  ApiBulkOrderVerificationError,
  ApiBulkOrderVerificationSuccess,
  ApiFeature,
} from 'app/modules/Remote/Feature/types';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiImageryContract, ApiProject} from 'app/modules/Remote/Project/types';
import {useCurrentImageryContract} from 'app/providers/ImageryContractProvider';
import * as CONSTANTS from 'app/utils/constants';
import * as costUtils from 'app/utils/costUtils';
import {useUserInfo} from 'app/utils/featureFlags';
import {formatFeatureName} from 'app/utils/featureUtils';
import {StatusMaybe, useTimestampMs} from 'app/utils/hookUtils';
import {LAYER_PACKAGES} from 'app/utils/layers';
import {parseLayerKey} from 'app/utils/layerUtils';
import {mayOrderImagery} from 'app/utils/userUtils';

import {useBulkSensingTime, usePlaceBulkOrder, useVerifyOrderImageryBulk} from './hooks';
import {ContractDetailsDescription, getPackageOrderText} from './orderImageryUtils';
import cs from './styles.styl';
import {usePushNotification} from '../Notification';

export const BulkOrderImageryModal: React.FunctionComponent<
  React.PropsWithChildren<{
    features: ApiFeature[];
    isOpen: boolean;
    onClose: () => void;
    onSubmit: () => void;
    layerKey: string;
    project: ApiProject;
    featureCollection: ApiFeatureCollection;
  }>
> = ({features, layerKey, isOpen = true, onClose, onSubmit, project, featureCollection}) => {
  const [getCurrentImageryContract] = useCurrentImageryContract();
  const [contractAfterMs] = useTimestampMs();
  const {sourceId} = parseLayerKey(layerKey);
  const pushNotification = usePushNotification();
  const [_, profile] = useUserInfo();

  const imageryContractStatusMaybe = getCurrentImageryContract(project.id, {
    afterMs: contractAfterMs,
    allowStaleMs: contractAfterMs - 60 * 1000,
  });
  const imageryContract =
    imageryContractStatusMaybe.value &&
    imageryContractStatusMaybe.value.contractType === CONSTANTS.CONTRACT_TYPE_DOLLAR_UNIT
      ? imageryContractStatusMaybe.value
      : null;

  const sensingTime = useBulkSensingTime(
    featureCollection.id,
    project.id,
    features.map((feature) => feature.id as number),
    sourceId
  );

  const {
    data: verifyOrderResponse,
    isLoading: isLoadingVerifyingOrder,
    isPending: isPendingVerifyOrder,
    isError: isErrorVerifyingOrder,
    error: verifyOrderError,
  } = useVerifyOrderImageryBulk({
    featureCollection,
    project,
    features: features.map((feature) => feature.id as number),
    sensingTime: sensingTime!,
    sourceId,
    enabled: !!sensingTime,
  });

  const scenes = verifyOrderResponse || [];
  const validScenes = scenes.filter((r) => 'costInCents' in r);
  const totalCostInCents = validScenes.reduce((accum, scene) => accum + scene.costInCents, 0);
  const errorScenes = scenes.filter((r) => 'error' in r);
  const alreadyPurchasedScenes = errorScenes.filter((s) => s.error.includes('already purchased'));
  const invalidScenes = errorScenes.filter((s) => s.error.includes('invalid scene'));

  const {mutateAsync: placeBulkOrder, isPending: isPlacingBulkOrder} = usePlaceBulkOrder({
    featureCollection,
    project,
    sensingTime: sensingTime!,
    sourceId,
  });

  const isVerifyingOrder = isPendingVerifyOrder || isLoadingVerifyingOrder;
  const canPurchase =
    imageryContract &&
    (validScenes?.length ?? 0) > 0 &&
    !isVerifyingOrder &&
    !isErrorVerifyingOrder &&
    !isPlacingBulkOrder;

  return (
    <ErrorBoundary
      fallback={
        <B.NonIdealState
          icon="error"
          iconMuted={false}
          title="Error"
          description={'Unable to load bulk order imagery modal.'}
        />
      }
    >
      <B.MultistepDialog
        className={classNames(cs.content, cs.bulkOrderModalContent)}
        isOpen={isOpen}
        onClose={onClose}
        title="Bulk Order Confirmation"
        finalButtonProps={{
          text: `Order for ${costUtils.formatCost(totalCostInCents)}`,
          disabled: !mayOrderImagery(profile.get('role')) || !canPurchase || isPlacingBulkOrder,
          onClick: async () => {
            const featureIds = validScenes?.map((scene) => scene.featureId) || [];
            await placeBulkOrder({
              featureIds,
              expectedCostInCents: totalCostInCents,
              pushNotification,
            });
            if (onSubmit) {
              onSubmit();
            }
          },
        }}
        backButtonProps={{
          text: 'Back',
          disabled: isPlacingBulkOrder,
        }}
      >
        <B.DialogStep
          id="calculate-cost"
          title="Calculate Cost"
          nextButtonProps={{
            disabled: !canPurchase || isVerifyingOrder,
            text: 'Review Order',
          }}
          panel={
            <CalculateCostPanel
              features={features}
              layerKey={layerKey}
              validScenes={validScenes}
              alreadyPurchasedScenes={alreadyPurchasedScenes}
              imageryContractStatusMaybe={imageryContractStatusMaybe}
              verifyOrderError={verifyOrderError}
              isVerifyingOrder={isVerifyingOrder}
            />
          }
        />
        <B.DialogStep
          id="review-order"
          title="Review Order"
          nextButtonProps={{
            text: 'Confirm Order',
            disabled: !canPurchase,
          }}
          panel={
            <ReviewOrderPanel
              features={features}
              validScenes={validScenes}
              alreadyPurchasedScenes={alreadyPurchasedScenes}
              invalidScenes={invalidScenes}
              layerKey={layerKey}
              imageryContract={imageryContract!} // Guaranteed to be non-null if we made it to this step because canPurchase is true
              isPlacingBulkOrder={isPlacingBulkOrder}
            />
          }
        />
      </B.MultistepDialog>
    </ErrorBoundary>
  );
};

const CalculateCostPanel = ({
  features,
  layerKey,
  validScenes,
  alreadyPurchasedScenes,
  imageryContractStatusMaybe,
  verifyOrderError,
  isVerifyingOrder,
}: {
  features: ApiFeature[];
  layerKey: string;
  validScenes: ApiBulkOrderVerificationSuccess[];
  alreadyPurchasedScenes: ApiBulkOrderVerificationError[];
  imageryContractStatusMaybe: StatusMaybe<ApiImageryContract | null>;
  verifyOrderError: Error | null;
  isVerifyingOrder: boolean;
}) => {
  const totalCostInCents = validScenes.reduce((accum, scene) => accum + scene.costInCents, 0);

  return (
    <div className={classNames(cs.content, cs.bulkOrderModalContent)}>
      {imageryContractStatusMaybe.status === 'error' ? (
        <B.NonIdealState
          icon="error"
          iconMuted={false}
          title="Error"
          description={'Unable to verify imagery contract.'}
        />
      ) : verifyOrderError && 'status' in verifyOrderError && verifyOrderError.status === 422 ? (
        <B.Callout intent={B.Intent.WARNING} title="Unable to order bulk selection">
          <p>
            You cannot place this order because there are not enough funds remaining for this
            portfolio.
          </p>
          <p>Please have an account administrator contact us for options.</p>
        </B.Callout>
      ) : verifyOrderError ? (
        <B.Callout intent={B.Intent.DANGER} title="Error calculating cost" icon={'error'}>
          <p>
            An error occurred while calculating the cost of your bulk order. Please try again or
            contact support if the issue persists.
          </p>
        </B.Callout>
      ) : isVerifyingOrder || imageryContractStatusMaybe.status === 'unknown' ? (
        <div className={cs.loadingSpinnerContainer}>
          <p>Calculating cost of bulk order for {features.length} properties and locations...</p>
          <B.Spinner className={cs.loadingSpinner} />
        </div>
      ) : (
        <div>
          {getPackageOrderText(layerKey, true)}
          <div className={cs.separator} />
          {alreadyPurchasedScenes.length === features.length ? (
            <B.Callout intent={B.Intent.DANGER} title="Unable to place bulk order" icon={'error'}>
              <p>
                This data has already been ordered for all {features.length} properties. Please
                adjust your selection.
              </p>
            </B.Callout>
          ) : (
            <>
              <B.Callout intent={B.Intent.WARNING} icon={null}>
                <p>
                  Your total comes to <strong>{costUtils.formatCost(totalCostInCents)}</strong>.
                  Your selection includes{' '}
                  <strong>{validScenes.length} properties and locations</strong> where this data has
                  not already been purchased. You will only be billed for the area within each
                  boundary, with vendor minimums applied.
                </p>
              </B.Callout>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ReviewOrderPanel = ({
  features,
  validScenes,
  invalidScenes,
  alreadyPurchasedScenes,
  layerKey,
  imageryContract,
  isPlacingBulkOrder,
}: {
  features: ApiFeature[];
  validScenes: ApiBulkOrderVerificationSuccess[];
  invalidScenes: ApiBulkOrderVerificationError[];
  alreadyPurchasedScenes: ApiBulkOrderVerificationError[];
  layerKey: string;
  imageryContract: ApiImageryContract;
  isPlacingBulkOrder: boolean;
}) => {
  const [_, profile] = useUserInfo();

  const totalCostInCents = validScenes.reduce((accum, scene) => accum + scene.costInCents, 0);

  const sortedValidScenes = validScenes.sort((a, b) => b.costInCents - a.costInCents);
  const allSortedScenes: ApiBulkOrderVerification[] = [
    ...sortedValidScenes,
    ...alreadyPurchasedScenes,
    ...invalidScenes,
  ];

  return (
    <div className={classNames(cs.content, cs.bulkOrderModalContent)}>
      {isPlacingBulkOrder ? (
        <div className={cs.loadingSpinnerContainer}>
          <p>Placing order...</p>
          <p>This may take a few minutes. Please don't refresh your page.</p>
          <B.Spinner className={cs.loadingSpinner} />
        </div>
      ) : (
        <div>
          <p>The table below shows the cost for each property or location in your order.</p>
          <div className={cs.tableContainer}>
            <table>
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {allSortedScenes.map((scene, index) => {
                  const feature = features.find((f) => f.id === scene.featureId);
                  let costString = '';
                  if ('costInCents' in scene) {
                    costString = costUtils.formatCost(scene.costInCents);
                  } else if (scene.error.includes('already purchased')) {
                    costString = '$0 (Already purchased)';
                  } else if (scene.error.includes('invalid scene')) {
                    costString = '$0 (Data not available)';
                  }
                  return (
                    <tr key={index}>
                      <td>{formatFeatureName(feature!)}</td>
                      <td>{costString}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className={cs.separator} />
          <ContractDetailsDescription
            isDisabledSource={!Object.keys(LAYER_PACKAGES).includes(layerKey)}
            sceneCoverageFailed={false} // This check happens in the api and will come back in the error scenes response
            canPurchase={true}
            layerKey={layerKey}
            orderGeometryAcres={0} // Unused so pass 0 for both acre props
            billableAcres={0}
            imageryContract={imageryContract!}
            role={profile.get('role')}
            orderCents={totalCostInCents}
            acresMessage={''}
            supportsCopyLink={false}
          />
        </div>
      )}
    </div>
  );
};
